import axios from 'axios';
import { API } from '../../../config/api';

export const http = axios.create({
  baseURL: API.REDIS,
  timeout: 10000
});

// :5100 requires a token now, and this instance would never have carried one.
//
// `setAuthToken` writes to `axios.defaults.headers.common`, but `axios.create()`
// snapshots the defaults at creation time — a value assigned later never reaches
// an instance that already exists. Calls made through the global `axios` object
// pick the header up; the ~18 modules that import this instance would not, and
// every one of them would have started returning 401.
//
// Reading localStorage per request also means login and logout take effect
// immediately, with no ordering assumptions between modules.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

/**
 * A `fetch` to :5100 that carries the token.
 *
 * The queue-editing actions — move, move-length, requeue-execution,
 * reallocate-produced — post with raw `fetch` and read `res.ok` / `res.json()`
 * themselves. They were written before :5100 required auth, so they sent no
 * header at all and every one of them began answering 401. That failure is
 * quiet in the worst way: the request goes out, nothing changes, and the screen
 * looks like the button simply did not work.
 *
 * This keeps their Response contract — callers still get a real `Response` —
 * and only adds the header, so the token still has exactly one home rather than
 * five copies of `localStorage.getItem`.
 *
 * @param {string} path absolute path, e.g. `/api/redis/queues/l75/move`
 */
export async function redisFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API.REDIS}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'x-auth-token': token } : {}),
      // caller's headers win, so an explicit Content-Type still applies
      ...(options.headers || {})
    }
  });
}
